"use client";

import { useRef } from "react";
import type { ReactNode } from "react";
import GridClearing from "@/components/GridClearing";
import { useRefGridMode } from "./RefGridContext";
import BridgeBand from "./BridgeBand";
import RowBand, { type RowBandVariant } from "./RowBand";

interface SectionShellProps {
  id?: string;
  /** `tight` = the reference `.section--tight` (84px vs 118px vertical padding). */
  variant?: "default" | "tight";
  /** Render a top hairline gradient divider (reference `.divider`). Ignored in
   *  grid mode — the body grid's own rows separate sections there. */
  divider?: boolean;
  /** Grid mode only: full-bleed row-snapped background band behind the section
   *  (translucent wash, or the opaque `dark` surface). Ignored off-grid. */
  band?: RowBandVariant;
  /** Grid mode only: keep the decoration field out of this whole section. */
  decoAvoid?: boolean;
  /** Grid mode only: dark pre-footer surface from the `[data-cta-card]`
   *  midline down — the closing "bridge into the footer" composition. */
  bridge?: boolean;
  className?: string;
  containerClassName?: string;
  children: ReactNode;
}

/**
 * Section wrapper: vertical rhythm + centered 1200px container (reference
 * `.section` + `.wrap`). Every reference section composes from this.
 *
 * Inside a `RefSections onGrid` band the shell switches to grid-paper layout
 * (the SmartRoutingSection pattern): on md+ the content column is the grid
 * container narrowed by one cell, so content starts ½ a cell inside the grid
 * line on each side, and a built-in `GridClearing` erases the complete cells
 * behind every `[data-grid-block]` descendant. Sections mark their own blocks.
 */
export default function SectionShell({
  id,
  variant = "default",
  divider = false,
  band,
  decoAvoid = false,
  bridge = false,
  className = "",
  containerClassName = "",
  children,
}: SectionShellProps) {
  const onGrid = useRefGridMode();
  const rootRef = useRef<HTMLDivElement>(null);

  if (onGrid) {
    return (
      <section
        id={id}
        data-deco-avoid={decoAvoid ? "" : undefined}
        className={`relative ${variant === "tight" ? "py-[84px]" : "py-[118px]"} ${className}`}
      >
        {band && <RowBand variant={band} />}
        {bridge && <BridgeBand />}
        <div
          ref={rootRef}
          className={`relative mx-auto w-full max-w-[1200px] px-7 md:w-[calc(var(--grid-container-width,1224px)_-_var(--grid-cell,72px))] md:max-w-full md:px-0 ${containerClassName}`}
        >
          <GridClearing rootRef={rootRef} />
          <div className="relative z-10">{children}</div>
        </div>
      </section>
    );
  }

  return (
    <>
      {divider && (
        <hr className="m-0 h-px border-0 bg-[linear-gradient(90deg,transparent,var(--color-line-2),transparent)]" />
      )}
      <section
        id={id}
        className={`relative ${variant === "tight" ? "py-[84px]" : "py-[118px]"} ${className}`}
      >
        <div
          className={`relative mx-auto w-full max-w-[1200px] px-7 ${containerClassName}`}
        >
          {children}
        </div>
      </section>
    </>
  );
}
